import { ChangeEvent, FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Pagination, Paper, Typography } from '@mui/material';
import classnames from 'classnames';

import Loadable from '@components/Loadable/Loadable';
import { InquiryEntryType } from '@interfaces/inquiries';
import {
  handleGetAdminMedicationDetails,
  handleGetAdminSupplementDetails,
} from '@store/features/adminInquiries/inquiryEntryDetails/actions';
import {
  adminActiveInquiryEntryTypeSelector,
  adminInquiryEntryDetailsActiveIdSelector,
} from '@store/features/adminInquiries/inquiryEntryDetails/selectors';
import { removeAdminSupplementInquiryDetails } from '@store/features/adminInquiries/inquiryEntryDetails/slice';
import { handleGetAdminInquiryList } from '@store/features/adminInquiries/inquiryList/actions';
import {
  adminInquiryListErrorSelector,
  adminInquiryListIsLoadingSelector,
  adminInquiryListPaginationSelector,
  adminInquiryListSelector,
} from '@store/features/adminInquiries/inquiryList/selectors';
import useDispatch from '@store/hooks/useDispatch';
import { EventType, useEventBus } from '@utils/eventBus';

import AsideFormCard from './AsideFormCard/AsideFormCard';
import InquiriesGridCard from './InquiriesGridCard/InquiriesGridCard';
import LegendCard from './LegendCard/LegendCard';
import { IAdminInquiryListProps } from './AdminInquiryList.interface';

import './style.scss';

const AdminInquiryList: FC<IAdminInquiryListProps> = ({ className, isPending }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const eventBus = useEventBus('adminInquiryList');

  const adminSearchInquiries = useSelector(adminInquiryListSelector);
  const adminSearchInquiriesPagination = useSelector(adminInquiryListPaginationSelector);
  const adminSearchInquiriesIsLoading = useSelector(adminInquiryListIsLoadingSelector);
  const adminSearchInquiriesError = useSelector(adminInquiryListErrorSelector);
  const adminSupplementInquiryActiveId = useSelector(adminInquiryEntryDetailsActiveIdSelector);
  const activeEntryType = useSelector(adminActiveInquiryEntryTypeSelector);

  useEffect(() => {
    dispatch(handleGetAdminInquiryList({ isPending }));
  }, [dispatch, isPending]);

  const handlePagination = useCallback(
    (event: ChangeEvent<unknown>, page: number) => {
      dispatch(handleGetAdminInquiryList({ page, isPending }));
    },
    [dispatch, isPending],
  );

  const handleRetryFetchAdminInquiryList = useCallback(() => {
    const page = adminSearchInquiriesPagination?.page;
    dispatch(handleGetAdminInquiryList({ page, isPending }));
  }, [adminSearchInquiriesPagination?.page, dispatch, isPending]);

  useEffect(() => {
    eventBus.on(
      [
        EventType.ADMIN_CREATE_NEW_SUPPLEMENT_SUCCESS,
        EventType.ADMIN_MATCH_EXISTING_SUCCESS,
        EventType.ADMIN_MARKED_AS_NEEDS_MORE_INFO_SUCCESS,
        EventType.ADMIN_MARKED_AS_MEDICATION_SUCCESS,
        EventType.ADMIN_RENEW_SUPPLEMENT_SUBMITTED,
        EventType.ADMIN_DEACTIVATE_SUPPLEMENT_SUCCESS,
        EventType.ADMIN_CREATE_NEW_MEDICATION_SUCCESS,
        EventType.ADMIN_MEDICATION_MARKED_AS_NEEDS_MORE_INFO_SUCCESS,
      ],
      () => {
        if (adminSupplementInquiryActiveId) {
          if (activeEntryType === InquiryEntryType.Supplement) {
            dispatch(handleGetAdminSupplementDetails(adminSupplementInquiryActiveId));
          }
          if (activeEntryType === InquiryEntryType.Medication) {
            dispatch(handleGetAdminMedicationDetails(adminSupplementInquiryActiveId));
          }
        }
        dispatch(
          handleGetAdminInquiryList({
            page: adminSearchInquiriesPagination?.page,
            isPending,
          }),
        );
      },
    );

    return () =>
      eventBus.off([
        EventType.ADMIN_CREATE_NEW_SUPPLEMENT_SUCCESS,
        EventType.ADMIN_MATCH_EXISTING_SUCCESS,
        EventType.ADMIN_MARKED_AS_NEEDS_MORE_INFO_SUCCESS,
        EventType.ADMIN_MARKED_AS_MEDICATION_SUCCESS,
        EventType.ADMIN_RENEW_SUPPLEMENT_SUBMITTED,
        EventType.ADMIN_DEACTIVATE_SUPPLEMENT_SUCCESS,
        EventType.ADMIN_CREATE_NEW_MEDICATION_SUCCESS,
        EventType.ADMIN_MEDICATION_MARKED_AS_NEEDS_MORE_INFO_SUCCESS,
      ]);
  }, [
    eventBus,
    dispatch,
    adminSupplementInquiryActiveId,
    adminSearchInquiriesPagination,
    isPending,
  ]);

  useEffect(
    () => () => {
      dispatch(removeAdminSupplementInquiryDetails());
    },
    [dispatch, isPending],
  );

  const tabTitleKey = isPending
    ? 'adminInquiries.numberOfInquiriesAwaitingResponse'
    : 'adminInquiries.numberOfInquiriesSentResponse';

  return (
    <Box className={classnames('admin-inquiry-list', className)}>
      <Box className="admin-inquiry-list__content">
        <div className="admin-inquiry-list__column admin-inquiry-list__column_list">
          <Typography variant="h4" className="admin-inquiry-list__column-title">
            {t(tabTitleKey, {
              num: adminSearchInquiriesPagination?.totalCount || 0,
            })}
          </Typography>

          <div className="admin-inquiry-list__table">
            <Loadable
              isLoading={adminSearchInquiriesIsLoading}
              error={adminSearchInquiriesError && t('errors.adminInquiryListError')}
              onRetry={handleRetryFetchAdminInquiryList}
            >
              <InquiriesGridCard data={adminSearchInquiries} />
            </Loadable>
          </div>

          {adminSearchInquiriesPagination && adminSearchInquiriesPagination.pageCount > 1 && (
            <Pagination
              className="admin-inquiry-list__pagination"
              color="primary"
              page={adminSearchInquiriesPagination.page}
              count={adminSearchInquiriesPagination.pageCount}
              onChange={handlePagination}
              showFirstButton
              showLastButton
            />
          )}

          <LegendCard />
        </div>

        <div className="admin-inquiry-list__column admin-inquiry-list__column_details">
          <Typography variant="h4" className="admin-inquiry-list__column-title">
            {t('adminInquiries.inquiry')}
          </Typography>
          <Paper className="admin-inquiry-list__details" elevation={0}>
            <AsideFormCard />
          </Paper>
        </div>
      </Box>
    </Box>
  );
};

export default AdminInquiryList;
